import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { MeFavoritesProductsQueryResult, MeQueryResult } from '@graphql/queries/__generated__/me.generated'
import React from 'react'
import CartFavorites from './components/CartFavorites'
import CartLinesContainer from './components/CartLinesContainer'
import CartLinesTotal from './components/CartLinesTotal'
import { TCartParams, TCartSetParams } from './types'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import LoginIcon from '@mui/icons-material/Login'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import CartPageLayout from '@components/CartPageLayout'

interface PageProps {
  isAuthenticated: boolean
  lineLoading: number | null
  cartQueryResult: CartQueryResult
  favoritesQueryResult: MeFavoritesProductsQueryResult
  meQueryResult: MeQueryResult
  params: TCartParams
  setParams: TCartSetParams

  handleSignIn(): void

  handleSignUp(): void

  setLineLoading(pk: number | null): void

  onClickProduct(specId: string | number, slug: string): void

  handleRemoveFromFavorites(productPk: number): Promise<void>
}

const Page: React.FC<PageProps> = ({
  handleSignIn,
  handleSignUp,
  cartQueryResult,
  isAuthenticated,
  lineLoading,
  setLineLoading,
  onClickProduct,
  favoritesQueryResult,
  handleRemoveFromFavorites,
  params,
  setParams,
  meQueryResult,
}) => {
  const { t } = useTranslation()
  return (
    <CartPageLayout
      headerExtra={
        !isAuthenticated && (
          <div className='hidden sm:flex items-center gap-8 text-13'>
            <span className='mr-10 hidden sm:inline'>{t('haveAnAccount', 'Have an account?')}</span>
            <Button
              type='button'
              variant='contained'
              color='secondary'
              onClick={handleSignIn}
              disableElevation
              disableRipple
              size='medium'
            >
              <LoginIcon fontSize='medium' className='mr-5' />
              <span className='text-13'>{t('signIn', 'Sign in')}</span>
            </Button>
            <span className='lowercase mx-5'>{t('or', 'or')}</span>
            <Button
              type='button'
              variant='outlined'
              color='secondary'
              onClick={handleSignUp}
              disableElevation
              disableRipple
              size='medium'
            >
              <AccountCircleOutlinedIcon fontSize='medium' className='mr-5' />
              <span className='text-13'>{t('signUp', 'Sign up')}</span>
            </Button>
          </div>
        )
      }
      lines={
        <CartLinesContainer
          isAuthenticated={isAuthenticated}
          lineLoading={lineLoading}
          setLineLoading={setLineLoading}
          onClickProduct={onClickProduct}
        />
      }
      belowLines={
        isAuthenticated && (
          <div className='my-24'>
            <CartFavorites
              favoritesQueryResult={favoritesQueryResult}
              onClickProduct={onClickProduct}
              favoritesLineLoading={lineLoading}
              handleRemoveFromFavorites={handleRemoveFromFavorites}
              page={params.favoritesPage || 1}
              setPage={(page: number) => setParams({ favoritesPage: page }, 'replaceIn')}
            />
          </div>
        )
      }
      sidebar={
        <CartLinesTotal
          isAuthenticated={isAuthenticated}
          cartQueryResult={cartQueryResult}
          handleSignIn={handleSignIn}
          loading={!!lineLoading || cartQueryResult.loading}
          meQueryResult={meQueryResult}
        />
      }
    />
  )
}

export default Page
